import React from 'react';
import { Chip, ThemeOptions } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { fieldTypes, chipTypes, statusSubCampaign, actionTakenSubCampaign } from '@/helpers/constants';
import { PlanStatus } from '@/types';
import styles from './UniversalChip.module.scss';
import HighlightText from '../../HighlightText/HighlightText';

const baseStyles = {
  root: {
    fontSize: 14,
    maxWidth: '100%',
  },
  rootActive: {},
  rootApproved: {},
  label: {},
  labelError: {},
};

const withFunctionStyles = (theme: ThemeOptions) => ({
  root: {
    ...baseStyles.root,
  },
  rootActive: {
    background: theme.cartTheme.menuLinkActive,
    color: theme.cartTheme.primaryColor,
    fontWeight: 'bold',
    '&:hover, &:focus': {
      background: theme.cartTheme.menuLinkActive,
    },
  },
  label: {
    lineHeight: '16px',
    padding: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    paddingLeft: '12px',
    paddingRight: '12px',
    textOverflow: 'ellipsis',
  },
  labelError: {
    color: theme.cartTheme.alertWarning,
  },
});

const classesTypes = {
  [chipTypes.WITHOUT_FUNCTION]: makeStyles<ThemeOptions>(baseStyles),
  [chipTypes.WITH_FUNCTION]: makeStyles<ThemeOptions>(withFunctionStyles),
  [chipTypes.READONLY]: makeStyles<ThemeOptions>({
    ...baseStyles,
    root: {
      ...baseStyles.root,
      whiteSpace: 'break-spaces',
    },
  }),
  [chipTypes.WITHOUT_FUNCTION_TABLE]: makeStyles<ThemeOptions>({
    ...baseStyles,
    root: {
      ...baseStyles.root,
      fontSize: 12,
      marginRight: 5,
    },
  }),
};

interface UniversalChipProps {
  chipType: string;
  active?: boolean;
  error?: boolean;
  text?: string;
  dot?: string;
  icon?: React.ReactElement;
  onClick?: () => void;
  id?: string;
  arrayStrings?: string[];
  classNameContainer?: string;
  fieldPath?: string[];
  noHighlightChip?: boolean;
  subCrStausColor?: string;
}

const UniversalChip: React.FC<UniversalChipProps> = (props) => {
  const { chipType, active = false, error = false, text = '', dot, icon, onClick = () => { }, id = '', arrayStrings = [],
    classNameContainer = '', fieldPath, noHighlightChip = false, subCrStausColor } = props;

  const chipTypeChecked = Object.keys(chipTypes).includes(chipType) ? chipType : chipTypes.READONLY;

  const classes = classesTypes[chipTypeChecked]();

  const getLabel = (withFunction: boolean, readOnly: boolean) => {
    if (noHighlightChip) {
      return (
        <>
          {readOnly ? arrayStrings.join('  ·  ') : text}
          {(withFunction && dot) && <span className={styles[dot]}>&#8226;</span>}
        </>
      );
    }

    return (
      <>
        <HighlightText
          fieldId={id}
          fieldPath={fieldPath}
          fieldType={fieldTypes.CHIP}
        >
          {readOnly ? arrayStrings.join('  ·  ') : text}
        </HighlightText>
        {(withFunction && dot) && <span className={styles[dot]}>&#8226;</span>}
      </>
    );
  };

  return (
    <div className={`${styles.ChipContainer} ${classNameContainer}`}>
      {chipTypeChecked === chipTypes.WITH_FUNCTION && (
        <Chip
          data-testid={id}
          classes={{
            root: !active ? `${classes.root} ${subCrStausColor} ` : `${classes.root} ${classes.rootActive} ${subCrStausColor}`,
            label: !error ? classes.label : `${classes.label} ${classes.labelError}`,
          }}
          label={
            getLabel(true, false)
          }
          component="a"
          icon={icon}
          onClick={onClick}
          id={id}
        />
      )}

      {chipTypeChecked === chipTypes.WITHOUT_FUNCTION && (
        <Chip
          data-testid={id}
          classes={classes}
          label={getLabel(false, false)}
          icon={icon}
          id={id}
        />
      )}

      {chipTypeChecked === chipTypes.READONLY && (
        <div className={classes.root} id={id} data-testid={id} onClick={onClick}>
          <span>{getLabel(false, true)}</span>
        </div>
      )}

      {chipTypeChecked === chipTypes.WITHOUT_FUNCTION_TABLE && (
        <Chip
          data-testid={id}
          classes={classes}
          size="small"
          label={text}
          icon={icon}
          id={id}
        />
      )}
    </div>
  );
};

export default UniversalChip;
